import { Accordion, Link, Typography } from "@platform/design-system";
import {
  Link as NavigationLink,
  type LinkProps as NavigationLinkProps,
} from "@platform/navigation-control";

import { RUN_STATE_TREATMENTS } from "../../runStateTreatment";
import { RunStateChip } from "../RunStateChip";

import type { RunStateKey } from "../../runStates";
import type { SectionLink, SectionMeta } from "../../types";

interface Props {
  title: string;
  state: RunStateKey;
  metas: readonly SectionMeta[];
  link?: SectionLink;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  "data-testid"?: string;
}

/**
 * Routes the design-system `Link` through the app's router, so a section link
 * changes tab without reloading the page. MUI hands its `component` an `href`;
 * the router takes `to`.
 */
const RoutedAnchor = ({
  href,
  ...rest
}: Omit<NavigationLinkProps, "to"> & { href: string }): React.JSX.Element => (
  <NavigationLink to={href} {...rest} />
);

/**
 * One collapsible Launchpad section: the design-system `Accordion` with the
 * section's counts and run state as its meta and the record link as its
 * header action.
 *
 * Headed `h3` because both containing cards title themselves with an `h2`.
 */
const LaunchpadSection = ({
  title,
  state,
  metas,
  link,
  defaultExpanded = false,
  children,
  "data-testid": dataTestId = "launchpad-section",
}: Props): React.JSX.Element => (
  <Accordion
    data-testid={dataTestId}
    headingComponent="h3"
    title={title}
    defaultExpanded={defaultExpanded}
    meta={
      <>
        {metas.map((meta) => (
          <Typography
            key={meta.label}
            component="span"
            variant="caption"
            color={meta.tone ? undefined : "textSecondary"}
            sx={
              meta.tone
                ? { color: RUN_STATE_TREATMENTS[meta.tone].textColor }
                : undefined
            }
          >
            {meta.label}
          </Typography>
        ))}
        <RunStateChip state={state} />
      </>
    }
    action={
      link && (
        // Caption weight: in the narrow General RFP Data column a
        // body-sized link crowds the header onto a second line.
        <Link
          component={RoutedAnchor}
          href={link.href}
          variant="caption"
          noWrap
        >
          {link.label}
        </Link>
      )
    }
  >
    {children}
  </Accordion>
);

export { LaunchpadSection };
export type { Props as LaunchpadSectionProps };
